import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  LinearProgress, Pagination, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
  Alert, Snackbar, MenuItem, Select, InputLabel, FormControl,
  IconButton, Avatar, Chip,
} from "@mui/material";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import ApartmentIcon from "@mui/icons-material/Apartment";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";

const DEFAULT_DEPARTMAN = { id: null, ad: "", aciklama: "" };
const DEFAULT_POZISYON  = { id: null, ad: "", aciklama: "", departman_id: "" };

const useBusy = () => {
  const [busy, setBusy] = useState(false);
  const wrap = async (fn) => { try { setBusy(true); return await fn(); } finally { setBusy(false); } };
  return { busy, wrap };
};

const Islemler = () => {
  const API_BASE_URL = useMemo(() => {
    const envUrl =
      (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
      (typeof process !== "undefined" && process.env?.REACT_APP_API_URL);
    return envUrl || "/api";
  }, []);

  const http = useMemo(() => {
    const handle = async (path, options = {}) => {
      const res = await fetch(`${API_BASE_URL}${path}`, {
        headers: { "Content-Type": "application/json", ...(options.headers || {}) },
        ...options,
      });
      const ct   = res.headers.get("content-type") || "";
      const data = ct.includes("application/json") ? await res.json() : null;
      if (!res.ok) {
        const e = new Error(data?.detail || res.statusText || "İstek başarısız");
        e.status = res.status; e.data = data; throw e;
      }
      return data;
    };
    return {
      get:  (p, params) => { const q = params ? `?${new URLSearchParams(params)}` : ""; return handle(p + q, { method: "GET" }); },
      post: (p, body)   => handle(p, { method: "POST",   body: JSON.stringify(body) }),
      put:  (p, body)   => handle(p, { method: "PUT",    body: JSON.stringify(body) }),
      del:  (p)         => handle(p, { method: "DELETE" }),
    };
  }, [API_BASE_URL]);

  const { busy, wrap } = useBusy();
  const [snack, setSnack] = useState({ open: false, type: "success", msg: "" });
  const ok  = (msg) => setSnack({ open: true, type: "success", msg });
  const err = (msg) => setSnack({ open: true, type: "error",   msg });

  const [activeTab, setActiveTab] = useState("departman");

  /* ---------- Departman ---------- */
  const [depItems,       setDepItems]       = useState([]);
  const [depLoading,     setDepLoading]     = useState(false);
  const [depError,       setDepError]       = useState("");
  const [depForm,        setDepForm]        = useState(DEFAULT_DEPARTMAN);
  const [depIsEdit,      setDepIsEdit]      = useState(false);
  const [depSearch,      setDepSearch]      = useState("");
  const [depPage,        setDepPage]        = useState(1);
  const [depConfirmOpen, setDepConfirmOpen] = useState(false);
  const [depToDelete,    setDepToDelete]    = useState(null);
  const [deletingDepId,  setDeletingDepId]  = useState(null);

  /* ---------- Pozisyon ---------- */
  const [pozItems,       setPozItems]       = useState([]);
  const [pozLoading,     setPozLoading]     = useState(false);
  const [pozError,       setPozError]       = useState("");
  const [pozForm,        setPozForm]        = useState(DEFAULT_POZISYON);
  const [pozIsEdit,      setPozIsEdit]      = useState(false);
  const [pozSearch,      setPozSearch]      = useState("");
  const [pozPage,        setPozPage]        = useState(1);
  const [pozConfirmOpen, setPozConfirmOpen] = useState(false);
  const [pozToDelete,    setPozToDelete]    = useState(null);
  const [deletingPozId,  setDeletingPozId]  = useState(null);
  const [pozDepFilter,   setPozDepFilter]   = useState("");

  const PAGE_SIZE = 10;

  /* ---------- Fetch ---------- */
  const fetchDepartmanlar = async () => {
    setDepLoading(true); setDepError("");
    try {
      const d = await http.get("/departmanlar", { skip: 0, limit: 50 });
      setDepItems(Array.isArray(d) ? d : []);
    } catch (e) { setDepError(e?.message || "Departman listesi alınamadı."); }
    finally { setDepLoading(false); }
  };

  const fetchPozisyonlar = async (departman_id) => {
    setPozLoading(true); setPozError("");
    try {
      const params = { skip: 0, limit: 50 };
      if (departman_id) params.departman_id = departman_id;
      const d = await http.get("/pozisyonlar", params);
      setPozItems(Array.isArray(d) ? d : []);
    } catch (e) { setPozError(e?.message || "Pozisyon listesi alınamadı."); }
    finally { setPozLoading(false); }
  };

  useEffect(() => { fetchDepartmanlar(); fetchPozisyonlar(); }, []);

  /* ---------- Filtre / Sayfalama ---------- */
  const depFiltered = useMemo(() => {
    const q = depSearch.toLowerCase();
    return q ? depItems.filter((it) => [it.ad, it.aciklama].filter(Boolean).some((v) => v.toLowerCase().includes(q))) : depItems;
  }, [depItems, depSearch]);

  const depPaged = useMemo(() => depFiltered.slice((depPage - 1) * PAGE_SIZE, depPage * PAGE_SIZE), [depFiltered, depPage]);

  const pozFiltered = useMemo(() => {
    const q = pozSearch.toLowerCase();
    let rows = pozDepFilter ? pozItems.filter((r) => String(r.departman_id) === String(pozDepFilter)) : pozItems;
    return q ? rows.filter((it) => [it.ad, it.aciklama].filter(Boolean).some((v) => v.toLowerCase().includes(q))) : rows;
  }, [pozItems, pozSearch, pozDepFilter]);

  const pozPaged = useMemo(() => pozFiltered.slice((pozPage - 1) * PAGE_SIZE, pozPage * PAGE_SIZE), [pozFiltered, pozPage]);

  /* ---------- Departman CRUD ---------- */
  const submitDep = async (e) => {
    e?.preventDefault?.();
    if (!depForm.ad || depForm.ad.trim().length < 2) return err("Departman adı en az 2 karakter olmalı.");
    const payload = { ad: depForm.ad.trim(), aciklama: depForm.aciklama || null };
    if (depIsEdit && depForm.id) {
      await wrap(async () => {
        try {
          const d = await http.put(`/departmanlar/${depForm.id}`, payload);
          ok("Departman güncellendi.");
          setDepItems((p) => p.map((it) => (it.id === d.id ? d : it)));
          setDepForm(DEFAULT_DEPARTMAN); setDepIsEdit(false);
        } catch (e) { err(e?.message || "Güncelleme başarısız."); }
      });
    } else {
      await wrap(async () => {
        try {
          const d = await http.post("/departmanlar", payload);
          ok("Departman oluşturuldu.");
          setDepItems((p) => [d, ...p]); setDepForm(DEFAULT_DEPARTMAN);
        } catch (e) { err(e?.status === 409 ? "Bu ad ile bir departman zaten var." : e?.message || "Oluşturma başarısız."); }
      });
    }
  };

  const doDeleteDep = async () => {
    if (!depToDelete) return;
    setDepConfirmOpen(false); setDeletingDepId(depToDelete.id);
    const original = [...depItems];
    setDepItems((p) => p.filter((it) => it.id !== depToDelete.id));
    setTimeout(async () => {
      try { await http.del(`/departmanlar/${depToDelete.id}`); ok("Departman silindi."); fetchPozisyonlar(pozDepFilter || undefined); }
      catch (e) { err(e?.message || "Silme başarısız."); setDepItems(original); }
      finally { setDeletingDepId(null); setDepToDelete(null); }
    }, 200);
  };

  /* ---------- Pozisyon CRUD ---------- */
  const submitPoz = async (e) => {
    e?.preventDefault?.();
    if (!pozForm.ad || pozForm.ad.trim().length < 2) return err("Pozisyon adı en az 2 karakter olmalı.");
    if (!pozForm.departman_id) return err("Lütfen departman seçiniz.");
    const payload = { ad: pozForm.ad.trim(), aciklama: pozForm.aciklama || null, departman_id: Number(pozForm.departman_id) };
    if (pozIsEdit && pozForm.id) {
      await wrap(async () => {
        try {
          await http.put(`/pozisyonlar/${pozForm.id}`, payload);
          ok("Pozisyon güncellendi.");
          await fetchPozisyonlar(pozDepFilter || undefined);
          setPozForm(DEFAULT_POZISYON); setPozIsEdit(false);
        } catch (e) { err(e?.message || "Güncelleme başarısız."); }
      });
    } else {
      await wrap(async () => {
        try {
          await http.post("/pozisyonlar", payload);
          ok("Pozisyon oluşturuldu.");
          await fetchPozisyonlar(pozDepFilter || undefined);
          setPozForm(DEFAULT_POZISYON);
        } catch (e) { err(e?.status === 409 ? "Bu departmanda bu ad ile pozisyon zaten var." : e?.message || "Oluşturma başarısız."); }
      });
    }
  };

  const doDeletePoz = async () => {
    if (!pozToDelete) return;
    setPozConfirmOpen(false); setDeletingPozId(pozToDelete.id);
    setTimeout(async () => {
      try { await http.del(`/pozisyonlar/${pozToDelete.id}`); ok("Pozisyon silindi."); await fetchPozisyonlar(pozDepFilter || undefined); }
      catch (e) { err(e?.message || "Silme başarısız."); }
      finally { setDeletingPozId(null); setPozToDelete(null); }
    }, 200);
  };

  /* ===================== RENDER ===================== */
  return (
    <Box>
      {/* Başlık + İstatistikler */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">İşlemler</Typography>
          <Typography variant="body1" color="text.secondary">Departman ve pozisyonları yönetin.</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, bgcolor: "#EFF6FF", px: 2, py: 1, borderRadius: 2 }}>
            <ApartmentIcon sx={{ fontSize: 18, color: "#2563EB" }} />
            <Typography fontSize={13} fontWeight={700} color="#2563EB">{depItems.length}</Typography>
            <Typography fontSize={12} color="#2563EB">Departman</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, bgcolor: "#F0FDF4", px: 2, py: 1, borderRadius: 2 }}>
            <BadgeOutlinedIcon sx={{ fontSize: 18, color: "#16A34A" }} />
            <Typography fontSize={13} fontWeight={700} color="#16A34A">{pozItems.length}</Typography>
            <Typography fontSize={12} color="#16A34A">Pozisyon</Typography>
          </Box>
        </Box>
      </Box>

      {/* Tab */}
      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        {[
          { key: "departman", label: "Departmanlar", count: depItems.length, icon: <ApartmentIcon sx={{ fontSize: 15 }} /> },
          { key: "pozisyon",  label: "Pozisyonlar",  count: pozItems.length, icon: <BadgeOutlinedIcon sx={{ fontSize: 15 }} /> },
        ].map((tab) => (
          <Box key={tab.key} onClick={() => setActiveTab(tab.key)} sx={{
            px: 3, py: 0.8, borderRadius: 2, cursor: "pointer", fontWeight: 600, fontSize: 14,
            display: "flex", alignItems: "center", gap: 1,
            bgcolor:     activeTab === tab.key ? "#EFF6FF" : "transparent",
            color:       activeTab === tab.key ? "#2563EB" : "text.secondary",
            border: "1px solid",
            borderColor: activeTab === tab.key ? "#BFDBFE" : "transparent",
            transition: "all 0.2s",
            "&:hover": { bgcolor: "#EFF6FF" },
          }}>
            {tab.icon} {tab.label}
            <Box sx={{ bgcolor: activeTab === tab.key ? "#2563EB" : "#E5E7EB", color: "white", borderRadius: 999, px: 1, fontSize: 11, fontWeight: 700 }}>
              {tab.count}
            </Box>
          </Box>
        ))}
      </Box>

      {/* ===== DEPARTMAN ===== */}
      {activeTab === "departman" && (
        <Stack spacing={2}>
          {/* Form — tek satır, tam genişlik */}
          <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid #E5E7EB" }} elevation={0}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              {depIsEdit ? "Departman Güncelle" : "Yeni Departman Ekle"}
            </Typography>
            <Box component="form" onSubmit={submitDep} noValidate>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label="Ad*" value={depForm.ad}
                  onChange={(e) => setDepForm((s) => ({ ...s, ad: e.target.value }))}
                  required inputProps={{ maxLength: 150 }} size="small" sx={{ flex: 1 }}
                />
                <TextField
                  label="Açıklama" value={depForm.aciklama}
                  onChange={(e) => setDepForm((s) => ({ ...s, aciklama: e.target.value }))}
                  size="small" sx={{ flex: 2 }}
                />
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button type="submit" variant="contained" disabled={busy}
                    startIcon={depIsEdit ? <EditNoteOutlinedIcon /> : <AddIcon />}
                    sx={{ borderRadius: 2, whiteSpace: "nowrap" }}>
                    {depIsEdit ? "Kaydet" : "Oluştur"}
                  </Button>
                  {depIsEdit && (
                    <Button variant="outlined" color="inherit" sx={{ borderRadius: 2 }}
                      onClick={() => { setDepForm(DEFAULT_DEPARTMAN); setDepIsEdit(false); }}>
                      İptal
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>
          </Paper>

          {/* Tablo — tam genişlik */}
          <Paper sx={{ borderRadius: 3, border: "1px solid #E5E7EB", overflow: "hidden" }} elevation={0}>
            <Box sx={{ px: 2.5, py: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #F3F4F6" }}>
              <Typography fontWeight={700} fontSize={15}>Departmanlar</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField size="small" placeholder="Ara..." value={depSearch}
                  onChange={(e) => { setDepSearch(e.target.value); setDepPage(1); }} sx={{ width: 200 }} />
                <Tooltip title="Yenile">
                  <IconButton size="small" onClick={fetchDepartmanlar} disabled={depLoading}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            {depLoading && <LinearProgress />}
            {depError && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{depError}</Alert>}
            <Table size="small">
              <TableHead sx={{ bgcolor: "#F9FAFB" }}>
                <TableRow>
                  {["Departman", "Açıklama", "Pozisyon Sayısı", "İşlemler"].map((h) => (
                    <TableCell key={h} sx={{ color: "gray", fontWeight: 600, fontSize: 12 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {depPaged.map((row) => {
                  const pozSayisi = pozItems.filter((p) => p.departman_id === row.id).length;
                  return (
                    <TableRow key={row.id} hover sx={{
                      transition: "opacity 0.2s, transform 0.2s",
                      opacity:   deletingDepId === row.id ? 0 : 1,
                      transform: deletingDepId === row.id ? "translateX(12px)" : "none",
                    }}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar sx={{ width: 30, height: 30, bgcolor: "#DBEAFE", fontSize: 12, color: "#2563EB" }}>
                            {(row.ad || "D")[0].toUpperCase()}
                          </Avatar>
                          <Typography fontSize={13} fontWeight={600}>{row.ad}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={row.aciklama || ""} sx={{ maxWidth: 500, color: "text.secondary" }}>
                          {row.aciklama || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={`${pozSayisi} pozisyon`} size="small" sx={{
                          bgcolor: pozSayisi > 0 ? "#F0FDF4" : "#F3F4F6",
                          color:   pozSayisi > 0 ? "#16A34A" : "#6B7280",
                          fontWeight: 600, fontSize: 11,
                        }} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Düzenle">
                            <IconButton size="small" onClick={() => { setDepForm({ id: row.id, ad: row.ad || "", aciklama: row.aciklama || "" }); setDepIsEdit(true); }}>
                              <EditNoteOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton size="small" color="error" onClick={() => { setDepToDelete(row); setDepConfirmOpen(true); }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {depPaged.length === 0 && !depLoading && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>Kayıt bulunamadı.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #F3F4F6" }}>
              <Typography variant="body2" color="text.secondary">Toplam: {depFiltered.length}</Typography>
              <Pagination count={Math.max(1, Math.ceil(depFiltered.length / PAGE_SIZE))} page={depPage} onChange={(_, v) => setDepPage(v)} size="small" color="primary" />
            </Box>
          </Paper>
        </Stack>
      )}

      {/* ===== POZİSYON ===== */}
      {activeTab === "pozisyon" && (
        <Stack spacing={2}>
          {/* Form — tek satır, tam genişlik */}
          <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid #E5E7EB" }} elevation={0}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              {pozIsEdit ? "Pozisyon Güncelle" : "Yeni Pozisyon Ekle"}
            </Typography>
            <Box component="form" onSubmit={submitPoz} noValidate>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl required size="small" sx={{ flex: 1 }}>
                  <InputLabel>Departman</InputLabel>
                  <Select label="Departman" value={pozForm.departman_id}
                    onChange={(e) => setPozForm((s) => ({ ...s, departman_id: e.target.value }))}>
                    {depItems.map((d) => <MenuItem key={d.id} value={d.id}>{d.ad}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField
                  label="Ad*" value={pozForm.ad}
                  onChange={(e) => setPozForm((s) => ({ ...s, ad: e.target.value }))}
                  required inputProps={{ maxLength: 150 }} size="small" sx={{ flex: 1 }}
                />
                <TextField
                  label="Açıklama" value={pozForm.aciklama}
                  onChange={(e) => setPozForm((s) => ({ ...s, aciklama: e.target.value }))}
                  size="small" sx={{ flex: 2 }}
                />
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button type="submit" variant="contained" disabled={busy}
                    startIcon={pozIsEdit ? <EditNoteOutlinedIcon /> : <AddIcon />}
                    sx={{ borderRadius: 2, whiteSpace: "nowrap" }}>
                    {pozIsEdit ? "Kaydet" : "Oluştur"}
                  </Button>
                  {pozIsEdit && (
                    <Button variant="outlined" color="inherit" sx={{ borderRadius: 2 }}
                      onClick={() => { setPozForm(DEFAULT_POZISYON); setPozIsEdit(false); }}>
                      İptal
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>
          </Paper>

          {/* Tablo — tam genişlik */}
          <Paper sx={{ borderRadius: 3, border: "1px solid #E5E7EB", overflow: "hidden" }} elevation={0}>
            <Box sx={{ px: 2.5, py: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #F3F4F6" }}>
              <Typography fontWeight={700} fontSize={15}>Pozisyonlar</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Departman Filtresi</InputLabel>
                  <Select label="Departman Filtresi" value={pozDepFilter}
                    onChange={(e) => { setPozDepFilter(e.target.value); setPozPage(1); }}>
                    <MenuItem value="">Tümü</MenuItem>
                    {depItems.map((d) => <MenuItem key={d.id} value={String(d.id)}>{d.ad}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField size="small" placeholder="Ara..." value={pozSearch}
                  onChange={(e) => { setPozSearch(e.target.value); setPozPage(1); }} sx={{ width: 200 }} />
                <Tooltip title="Yenile">
                  <IconButton size="small" onClick={() => fetchPozisyonlar(pozDepFilter || undefined)} disabled={pozLoading}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            {pozLoading && <LinearProgress />}
            {pozError && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{pozError}</Alert>}
            <Table size="small">
              <TableHead sx={{ bgcolor: "#F9FAFB" }}>
                <TableRow>
                  {["Pozisyon", "Departman", "Açıklama", "İşlemler"].map((h) => (
                    <TableCell key={h} sx={{ color: "gray", fontWeight: 600, fontSize: 12 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pozPaged.map((row) => {
                  const depName = depItems.find((d) => d.id === row.departman_id)?.ad || "-";
                  return (
                    <TableRow key={row.id} hover sx={{
                      transition: "opacity 0.2s, transform 0.2s",
                      opacity:   deletingPozId === row.id ? 0 : 1,
                      transform: deletingPozId === row.id ? "translateX(12px)" : "none",
                    }}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar sx={{ width: 30, height: 30, bgcolor: "#F0FDF4", fontSize: 12, color: "#16A34A" }}>
                            {(row.ad || "P")[0].toUpperCase()}
                          </Avatar>
                          <Typography fontSize={13} fontWeight={600}>{row.ad}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={depName} size="small" sx={{ bgcolor: "#EFF6FF", color: "#2563EB", fontWeight: 600, fontSize: 11 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={row.aciklama || ""} sx={{ maxWidth: 500, color: "text.secondary" }}>
                          {row.aciklama || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Düzenle">
                            <IconButton size="small" onClick={() => { setPozForm({ id: row.id, ad: row.ad || "", aciklama: row.aciklama || "", departman_id: row.departman_id || "" }); setPozIsEdit(true); }}>
                              <EditNoteOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton size="small" color="error" onClick={() => { setPozToDelete(row); setPozConfirmOpen(true); }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {pozPaged.length === 0 && !pozLoading && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>Kayıt bulunamadı.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #F3F4F6" }}>
              <Typography variant="body2" color="text.secondary">Toplam: {pozFiltered.length}</Typography>
              <Pagination count={Math.max(1, Math.ceil(pozFiltered.length / PAGE_SIZE))} page={pozPage} onChange={(_, v) => setPozPage(v)} size="small" color="primary" />
            </Box>
          </Paper>
        </Stack>
      )}

      {/* Departman Silme Onayı */}
      <Dialog open={depConfirmOpen} onClose={() => setDepConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Departman Silinsin mi?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            <strong>{depToDelete?.ad}</strong> departmanını silmek istediğinizden emin misiniz?
            İlişkili pozisyonlar varsa işlem başarısız olabilir.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDepConfirmOpen(false)}>Vazgeç</Button>
          <Button variant="contained" color="error" onClick={doDeleteDep} disabled={busy}>Evet, Sil</Button>
        </DialogActions>
      </Dialog>

      {/* Pozisyon Silme Onayı */}
      <Dialog open={pozConfirmOpen} onClose={() => setPozConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Pozisyon Silinsin mi?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            <strong>{pozToDelete?.ad}</strong> pozisyonunu silmek istediğinizden emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPozConfirmOpen(false)}>Vazgeç</Button>
          <Button variant="contained" color="error" onClick={doDeletePoz} disabled={busy}>Evet, Sil</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.type} variant="filled" sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Islemler;
